import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { waLink } from "../lib/whatsapp";

const links = [
  { to: "/", label: "Início" },
  {
    label: "Soluções",
    children: [
      { to: "/outsourcing", label: "Outsourcing", desc: "Gestão completa do parque de impressão" },
    ],
  },
  { to: "/produtos", label: "Produtos" },
  { to: "/sobre", label: "Quem Somos" },
  { to: "/contato", label: "Contato" },
];

const HOVER = "text-[#1A1A1A] hover:text-[#E84E34] transition-colors";
const ACTIVE = "text-[#E84E34]";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false); // mobile only

  return (
    <header
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#E5E7EB]"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-4 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.children ? (
              <DesktopDropdown key={l.label} item={l} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-xs font-bold uppercase tracking-[0.18em] ${isActive ? ACTIVE : HOVER}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <a
            href={waLink("Olá Avantech, gostaria de solicitar um orçamento.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-whatsapp"
            className="bg-[#E84E34] text-white text-xs font-bold uppercase tracking-[0.18em] px-5 py-3 hover:bg-[#CF412A] transition-colors"
          >
            Solicitar Orçamento
          </a>
        </nav>

        <button
          className="md:hidden p-2 hover:text-[#E84E34] transition-colors"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) =>
              l.children ? (
                <div key={l.label}>
                  <button
                    onClick={() => setSubmenuOpen((v) => !v)}
                    data-testid="mobile-nav-solucoes"
                    className={`w-full flex items-center justify-between text-sm font-bold uppercase tracking-[0.18em] py-1 ${HOVER}`}
                  >
                    {l.label}
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${submenuOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {submenuOpen && (
                    <div className="mt-2 ml-4 pl-3 border-l-2 border-[#E84E34] flex flex-col gap-3">
                      {l.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-${c.to.replace("/", "")}`}
                          className={({ isActive }) =>
                            `text-sm font-bold uppercase tracking-[0.18em] ${isActive ? ACTIVE : HOVER}`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `text-sm font-bold uppercase tracking-[0.18em] ${isActive ? ACTIVE : HOVER}`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
            <a
              href={waLink("Olá Avantech, gostaria de solicitar um orçamento.")}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#E84E34] text-white text-sm font-bold uppercase tracking-[0.18em] px-5 py-3 text-center mt-2"
            >
              Solicitar Orçamento
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const DesktopDropdown = ({ item }) => {
  const [show, setShow] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <button
        type="button"
        data-testid="nav-solucoes"
        className={`flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] ${HOVER}`}
      >
        {item.label}
        <ChevronDown size={14} strokeWidth={2.2} className={`transition-transform ${show ? "rotate-180" : ""}`} />
      </button>
      {show && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-72"
          data-testid="nav-solucoes-menu"
        >
          <div className="bg-white border border-[#E5E7EB] shadow-2xl border-t-2 border-t-[#E84E34]">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                data-testid={`nav-${c.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `block px-5 py-4 hover:bg-[#F9F9F9] border-b border-[#E5E7EB] last:border-b-0 group ${
                    isActive ? "bg-[#F9F9F9]" : ""
                  }`
                }
              >
                <div className={`text-sm font-bold uppercase tracking-[0.12em] ${HOVER}`}>{c.label}</div>
                {c.desc && (
                  <div className="text-xs text-[#6B7280] mt-1 normal-case tracking-normal">{c.desc}</div>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
